import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { AppContext } from '../../contexts/app-context';
import { ArrowRightAltIcon, WaveLightImage, WaveLightSmallImage } from '../../utils/icons';
import { Avatar, Box, Grid } from '../../utils/material-ui-core';
import CookieConsentConfirmationButton from '../cookie-consent';
import SplashWait from '../splash-wait';
import TermsConditions from '../terms-conditions';

type IScreenProps = {
  children: JSX.Element | JSX.Element[];
};

const Screen = ({ children }: IScreenProps) => {
  const { contentDataLoading } = useContext(AppContext);
  const pageStyles = LocalPageStyles();

  const [showGoToTopButton, setGoToTopButton] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (window.pageYOffset > 300) {
        setGoToTopButton(true);
      } else {
        setGoToTopButton(false);
      }
    });
  }, []);

  return (
    <React.Fragment>
      {contentDataLoading ? (
        <SplashWait />
      ) : (
        <React.Fragment>
          {showGoToTopButton ? (
            <button onClick={scrollToTop} className={pageStyles.goToTop}>
              <Avatar variant="rounded" className={pageStyles.avatar}>
                <ArrowRightAltIcon />
              </Avatar>
            </button>
          ) : null}

          <CookieConsentConfirmationButton />

          <Grid container spacing={0}>
            <Grid item xs={12} zeroMinWidth>
              <Box className={pageStyles.screenContainer}>
                <Box p={0} className={pageStyles.screenContent}>
                  {children}
                  <Box p={7}>&nbsp;</Box>
                </Box>

                <Box className={pageStyles.footer}>
                  <div className={pageStyles.waves}>
                    <div className="wave" id="wave1"></div>
                    <div className="wave" id="wave2"></div>
                    <div className="wave" id="wave3"></div>
                    <div className="wave" id="wave4"></div>
                  </div>
                  <TermsConditions />

                </Box>
              </Box>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const hexToRgb = (hex) => {
  hex = hex.replace('#', '');
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `${r}, ${g}, ${b}`;
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    screenContainer: {
      display: "flex",
      flexDirection: "column",
      "& .MuiBreadcrumbs-ol": {
        paddingTop: 10,
      },

      [theme.breakpoints.down("xs")]: {
        "& .MuiDialog-paperFullWidth": {
          width: '100% !important',
          marginLeft: "0px !important",
          marginRight: "0px !important",
        }
      },

    },
    screenContent: {
      margin: 'auto',
      minHeight: "calc(100vh - 210px)",
      width: "100%",
      overflow: 'hidden',
      flexGrow: 1,
      flex: 1,
      marginTop: "100px",
      marginBottom: "100px",
      paddingLeft: 5,
      [theme.breakpoints.down("xs")]: {
        minHeight: "calc(100vh - 150px)",
        padding: "0px 0px 10px 0px",
      },
      "& ::-webkit-scrollbar": {
        width: "10px",
      },
      "& ::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
      "& ::-webkit-scrollbar-thumb": {
        borderRadius: "5px",
        borderWidth: "3px",
        borderStyle: "solid",
        borderColor: "transparent",
        backgroundClip: "content-box",
        backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[600] : `rgba(${hexToRgb(theme.palette.secondary.dark)},0.4)`,
      },
      "& .highcharts-credits": {
        display: "none",
      },
      "& .MuiAlert-root": {
        borderRadius: 0,
      },
      "& .MuiAlert-outlinedWarning": {
        borderLeftWidth: "5px",
      },
      "& .MuiAlert-outlinedError": {
        borderLeftWidth: "5px",
      },
      "& .MuiAlert-outlinedInfo": {
        borderLeftWidth: "5px",
      },
      "& .MuiDialogContent-root": {
        scrollbarColor: "#6b6b6b",
        "&::-webkit-scrollbar": {
          width: "10px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
          borderRadius: "10px",
          borderWidth: "5px",
          borderStyle: "solid",
          borderColor: "transparent",
          backgroundClip: "content-box",
          backgroundColor: "white",
        },
      },
      "& .MuiGridPanel-root": {
        border: '10px red solid'
      },
    },
    screenFooter: {
      paddingTop: 30,
      paddingBottom: 60,
      color: "#000000 !important",
      marginBottom: 5,
    },
    footer: {
      marginTop: "50px",
      position: "relative",
      background: "#E0E0E0",
      minHeight: "100px",
      padding: "20px 0px 20px 0px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      "& p": {
        color: "#fff",
        margin: "15px 0 10px 0",
        fontSize: "1rem",
        fontWeight: "300",
      }
    },
    waves: {
      "& .wave": {
        position: "absolute",
        top: "-100px",
        left: 0,
        width: "100%",
        height: "100px",
        background: `url(${WaveLightImage})`,
        backgroundSize: "1000px 100px",
        [theme.breakpoints.down("xs")]: {
          background: `url(${WaveLightSmallImage})`,
          backgroundSize: "700px 100px",
        },

      },
      "& #wave1": {
        zIndex: 1000,
        opacity: 1,
        bottom: 0,
        animation: "$animateWaves 10s linear infinite",
      },
      "& #wave2": {
        zIndex: 999,
        opacity: 0.2,
        bottom: "10px",
        animation: "$animate 12s linear infinite !important",
      },
      "& #wave3": {
        zIndex: 1000,
        opacity: 0.5,
        bottom: "15px",
        animation: "$animateWaves 8s linear infinite",
      },
      "& #wave4": {
        zIndex: 999,
        opacity: 0.7,
        bottom: "20px",
        animation: "$animate 15s linear infinite",
      }
    },

    "@keyframes animateWaves": {
      '0%': {
        backgroundPositionX: '1000px',
      },
      '100%': {
        backgroundPositionX: '0px',
      },
    },

    "@keyframes animate": {
      '0%': {
        backgroundPositionX: '-1000px',
      },
      '100%': {
        backgroundPositionX: '0px',
      },
    },
    goToTop: {
      textAlign: "center",
      padding: 0,
      zIndex: 9999,
      position: "fixed",
      bottom: 20,
      right: 20,
      backgroundColor: "transparent",
      cursor: "pointer",
      border: "none",
      transform: "rotate(270deg)",

      "& > :hover": {
        backgroundColor: theme.palette.secondary.main,
      },
    },
    avatar: {
      backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[600] : theme.palette.grey[800],
    },
  })
);

export default Screen;
